import { useNavigate, useLocation } from "react-router-dom";
import { useStore } from "../contexts/StoreContext";
import { useQuery } from "@tanstack/react-query";
import { fetchStoreSetup } from "../api/queries";
import CreateStore from "../pages/stores/new";
import NewPass from "../pages/passes/NewPass";
import NewForm from "../pages/forms/new";
import NewFlyer from "../pages/flyers/new";
import EditFlyer from "../pages/flyers/edit";
import EditPass from "./passes/EditPass";
import EditForm from "../pages/forms/edit";
import EditStore from "../components/EditStore";
import Pricing from "../pages/Pricing";
import ProgressBar from "../blocks/progressBar";

import { fetchPasses } from "../api/queries";
import { fetchForms } from "../api/queries";
import { fetchFlyers } from "../api/queries";

import {
  ArrowDownTrayIcon,
  ShareIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import DownloadQR from "../assets/download.png";
import { QRCodeSVG } from "qrcode.react";
import { useState, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import React, { createContext } from "react";
import Button from "../components/Button";

// First, make showProgressBar available to other components through context
export const SetupContext = createContext();

const useDeviceType = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  return isMobile;
};

export default function Setup() {
  const { selectedStore } = useStore();
  const { t } = useLanguage();
  const location = useLocation();
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [currentStepState, setCurrentStepState] = useState(null);
  const navigate = useNavigate();

  const { data: setupStatus } = useQuery({
    queryKey: ["setup-status", selectedStore?.id],
    queryFn: () => fetchStoreSetup(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const currentStep = location.pathname.split("/").pop();

  const steps = [
    {
      id: "1",
      name: t("setup.steps.store.title"),
      href: "/setup/store",
      description: t("setup.steps.store.description"),
      status: setupStatus?.has_store ? "complete" : "upcoming",
      component: <CreateStore />,
      editComponent: <EditStore />,
      step: "store",
    },
    {
      id: "2",
      name: t("setup.steps.pass.title"),
      href: "/setup/pass",
      description: t("setup.steps.pass.description"),
      status: setupStatus?.has_pass ? "complete" : "upcoming",
      component: <NewPass />,
      editComponent: <EditPass />,
      step: "pass",
    },
    {
      id: "3",
      name: t("setup.steps.form.title"),
      href: "/setup/form",
      description: t("setup.steps.form.description"),
      status: setupStatus?.has_form ? "complete" : "upcoming",
      component: <NewForm />,
      editComponent: <EditForm />,
      step: "form",
    },
    {
      id: "4",
      name: t("setup.steps.flyers.title"),
      href: "/setup/flyers",
      description: t("setup.steps.flyers.description"),
      status: setupStatus?.has_flyers ? "complete" : "upcoming",
      component: <NewFlyer />,
      editComponent: <EditFlyer />,
      step: "flyers",
    },
  ];

  // Find the first incomplete step
  const currentStepIndex = steps.findIndex(
    (step) => step.status === "upcoming",
  );

  steps.forEach((step) => {
    if (step.status === "upcoming") {
      // If we're on a specific step URL, mark that step as current
      if (step.step === currentStep) {
        step.status = "current";
      }
      // If we're on the main setup URL, mark the first incomplete step as current
      else if (
        currentStep === "setup" &&
        currentStepIndex === parseInt(step.id) - 1
      ) {
        step.status = "current";
      }
    }
  });

  const setStepEditingId = async (step) => {
    let id;
    switch (step) {
      case "store":
        id = selectedStore?.id;
        break;
      case "pass":
        const passes = await fetchPasses(selectedStore?.id);
        id = passes?.[0]?.id;
        break;
      case "form":
        const forms = await fetchForms(selectedStore?.id);
        id = forms?.[0]?.id;
        break;
      case "flyers":
        const flyers = await fetchFlyers(selectedStore?.id);
        id = flyers?.[0]?.id;
        break;
    }

    setEditingId(id);
    setCurrentStepState(step);
  };

  useEffect(() => {
    const fetchStepData = async () => {
      setEditingId(null);
      setCurrentStepState(null);
      // On /setup/* routes StoreCheckWrapper renders before stores load, so
      // selectedStore can be undefined here. Bail until it resolves to avoid
      // firing /stores/undefined/passes (etc.) → 404. The effect re-runs once
      // selectedStore?.id is set (it's a dependency below).
      if (!selectedStore?.id) return;
      await setStepEditingId(currentStep);
    };
    fetchStepData();
  }, [currentStep, selectedStore?.id]);

  // Add redirect logic for incomplete steps
  useEffect(() => {
    if (currentStep !== "setup" && setupStatus) {
      // Check the original completion status directly from setupStatus
      let shouldRedirect = false;

      switch (currentStep) {
        case "store":
          shouldRedirect = !setupStatus.has_store;
          break;
        case "pass":
          shouldRedirect = !setupStatus.has_pass;
          break;
        case "form":
          shouldRedirect = !setupStatus.has_form;
          break;
        case "flyers":
          shouldRedirect = !setupStatus.has_flyers;
          break;
      }

      if (shouldRedirect) {
        navigate("/setup");
      }
    }
  }, [currentStep, setupStatus, navigate]);

  // Add payment success tracking
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentStatus = urlParams.get("payment");

    if (paymentStatus === "success") {
      // Send GTM event
      const prices = {
        month: {
          starter: 19,
          growth: 39,
          advanced: 79,
        },
        year: {
          starter: 190,
          growth: 390,
          advanced: 790,
        },
      };

      if (selectedStore) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "purchase",
          transaction_id: selectedStore?.stripe_subscription_id, // Unique ID
          value:
            prices[selectedStore?.billing_cycle][selectedStore?.current_plan], // Total revenue (without tax/shipping)
          currency: "EUR",
          items: [
            {
              item_id:
                selectedStore?.current_plan +
                "_" +
                selectedStore?.billing_cycle,
              item_name: selectedStore?.current_plan,
              price:
                prices[selectedStore?.billing_cycle][
                  selectedStore?.current_plan
                ],
              quantity: 1,
              subscription_term: selectedStore?.billing_cycle,
            },
          ],
        });

        // Clear URL parameters
        const newUrl = location.pathname;
        navigate(newUrl, { replace: true });
      }
    }
  }, [location.search, navigate, selectedStore?.id]);

  const CompletionView = () => {
    const isMobile = useDeviceType();
    const { selectedStore } = useStore();
    const { data: flyers } = useQuery({
      queryKey: ["flyers", selectedStore?.id],
      queryFn: () => fetchFlyers(selectedStore?.id),
      enabled: !!selectedStore?.id,
    });

    const lastFlyer = flyers?.[0];

    const { data: forms } = useQuery({
      queryKey: ["forms", selectedStore?.id],
      queryFn: () => fetchForms(selectedStore?.id),
      enabled: !!selectedStore?.id,
    });

    const lastForm = forms?.[0];

    const downloadQRCode = async (form) => {
      const qrValue = `${window.location.origin}/f/${form?.uuid}`;
      const svgString = document.getElementById(`qr-${form.id}`).innerHTML;
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);

      downloadFile(url, `qr-code-${form.id}.svg`);
      URL.revokeObjectURL(url);
    };

    const downloadFile = (url, name = null) => {
      const link = document.createElement("a");
      link.href = url;
      link.download = name ? name : url.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <>
        {setupStatus?.setup_completed ? (
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h2 className="text-5xl">🎉</h2>
              <h3 className="text-lg font-medium text-gray-900">
                {t("setup.completion.title")}
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                {t("setup.completion.subtitle")}
              </p>
            </div>
            <div>
              {/* Step 1: Flyer */}
              <h3 className="text-center text-base font-medium text-gray-900 mb-4">
                <div className="mr-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white font-medium">
                  1
                </div>
                {t("setup.completion.promoteProgram.header")}
              </h3>
              <div className="grid lg:grid-cols-3 gap-6 mb-8">
                <div className="grid content-between gap-4 p-4 bg-white rounded-lg shadow-md text-center text-sm text-gray-500">
                  <div>
                    <h4 className="text-base font-medium text-gray-900 mb-4">
                      {t("setup.completion.promoteProgram.url.title")}
                    </h4>
                    <p>
                      {t("setup.completion.promoteProgram.url.description")}
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      onClick={() => {
                        navigator
                          .share({
                            title: t("public.referral.shareMessage.title"),
                            url: `${window.location.origin}/f/${lastForm.uuid}`,
                            text: t("public.referral.shareMessage.text"),
                          })
                          .catch((error) => {
                            // Silently handle abort errors when user cancels share dialog
                            if (error.name !== "AbortError") {
                              console.error("Error sharing:", error);
                            }
                          });
                      }}
                      icon={ShareIcon}
                    >
                      {t("public.referral.shareButton")}
                    </Button>

                    <Button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/f/${lastForm.uuid}`,
                        );
                        setCopied(true);
                      }}
                      icon={LinkIcon}
                      variant="terriary"
                    >
                      {copied
                        ? t("public.referral.copied")
                        : t("public.referral.copyButton")}
                    </Button>
                  </div>
                </div>
                <div className="grid content-between gap-4 p-4 bg-white rounded-lg shadow-md text-center text-sm text-gray-500">
                  <div>
                    <h4 className="text-base font-medium text-gray-900 mb-4">
                      {t("setup.completion.promoteProgram.qr.title")}
                    </h4>
                    <p>{t("setup.completion.promoteProgram.qr.description")}</p>
                  </div>
                  <div>
                    <div id={`qr-${lastForm?.id}`} className="hidden">
                      <QRCodeSVG
                        value={`${window.location.origin}/f/${lastForm?.uuid}`}
                        size={64}
                        level="L"
                      />
                    </div>
                    <Button
                      onClick={() => downloadQRCode(lastForm)}
                      icon={ArrowDownTrayIcon}
                    >
                      {t("setup.completion.promoteProgram.qr.button")}
                    </Button>
                  </div>
                </div>
                <div className="grid content-between gap-4 p-4 bg-white rounded-lg shadow-md text-center text-sm text-gray-500">
                  <div>
                    <h4 className="text-base font-medium text-gray-900 mb-4">
                      {t("setup.completion.promoteProgram.flyer.title")}
                    </h4>
                    <p>
                      {t("setup.completion.promoteProgram.flyer.description")}
                    </p>
                  </div>
                  <div>
                    <Button
                      onClick={() =>
                        downloadFile(
                          lastFlyer?.pdf_url,
                          `flyer-${lastFlyer?.id}.pdf`,
                        )
                      }
                      icon={ArrowDownTrayIcon}
                    >
                      {t("setup.completion.promoteProgram.flyer.button")}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Step 2: App */}
              <h3 className="text-center text-base font-medium text-gray-900 mb-4">
                <div className="mr-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white font-medium">
                  2
                </div>
                {t("setup.completion.downloadApp.title")}
              </h3>
              <div className="bg-white p-4 rounded-lg mx-auto shadow-md">
                <p className="text-center text-sm text-gray-500">
                  {t("setup.completion.downloadApp.description")}
                </p>
                <div className="overflow-hidden flex flex-col">
                  <div className="flex-1 flex flex-col">
                    <div className="mt-4 flex flex-col items-center space-y-3">
                      {!isMobile ? (
                        <>
                          <img
                            src={DownloadQR}
                            alt="Download QR"
                            className="max-w-xs w-[240px] h-[240px]"
                          />
                          <p
                            className="text-sm text-gray-600 text-center"
                            dangerouslySetInnerHTML={{
                              __html: t(
                                "setup.completion.downloadApp.qrDescription",
                              ),
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <p className="p-4 text-sm text-gray-600 text-center">
                            {t(
                              "setup.completion.downloadApp.mobileDescription",
                            )}{" "}
                            <a
                              href="https://download.loyaltify.io"
                              target="_blank"
                              className="font-medium text-indigo-600 mx-1"
                            >
                              {t("setup.completion.downloadApp.downloadLink")}
                            </a>{" "}
                            {t("setup.completion.downloadApp.onMobile")}
                          </p>
                          <a
                            className="inline-flex items-center px-6 py-2.5 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-150"
                            target="_blank"
                            href="https://download.loyaltify.io"
                          >
                            <svg
                              className="w-5 h-5 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                            {t("setup.completion.downloadApp.button")}
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <Pricing setupMode={true} />
        )}
      </>
    );
  };

  const renderCurrentStep = () => {
    if (currentStep !== "setup") {
      const editingStep = steps.find((step) => step.step === currentStep);

      if (editingStep?.status === "complete" && editingStep?.editComponent) {
        const EditComponent = editingStep.editComponent.type;

        steps.forEach((step) => {
          if (step.step === currentStep) {
            step.status = "current";
          }
        });

        if (currentStepState !== currentStep || !editingId) {
          return (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading...</div>
            </div>
          );
        }

        return (
          <>
            <ProgressBar steps={steps} className="mb-4" />
            <EditComponent id={editingId} setupMode={true} />
          </>
        );
      }
    }

    if (currentStepIndex === -1) {
      return <CompletionView />;
    }

    if (currentStepIndex >= 0 && currentStepIndex < steps.length) {
      return (
        <>
          <ProgressBar steps={steps} />
          {steps[currentStepIndex].component}
        </>
      );
    }

    return <CompletionView />;
  };

  return (
    <SetupContext.Provider value={{ showProgressBar }}>
      <div>
        {/* Current Step Content */}
        <div className="max-w-8xl mx-auto">{renderCurrentStep()}</div>
      </div>
    </SetupContext.Provider>
  );
}
